import { useCallback, useEffect } from "react";

import { useEditor, useNode } from "@craftjs/core";
import { PaintbrushVertical, Pencil, PencilOff } from "lucide-react";
import { Button, ButtonGroup, DefaultTooltip, InputGroup, Select } from "twyne-new.ui";

import { usePureCraftProp } from "../../utils";

import { useStylePatternOptions, useStylePatternUpdater } from "./hooks";

export default function StylePatternProperty({ name, isFromShared }) {
  const { value: pattern, setValue: setPattern } = usePureCraftProp("properties.stylePattern", name || undefined)
  const { actions: { setCustom } } = useEditor()
  const { enabledUpdate, id } = useNode(({ data: { custom } }) => ({
    enabledUpdate: custom.stylePatternAutoUpdate,
  }))

  const { create, update } = useStylePatternUpdater(enabledUpdate, pattern)
  const options = useStylePatternOptions()

  const setEnabledUpdate = useCallback(value => {
    setCustom(id, custom => {
      custom.stylePatternAutoUpdate = isFromShared ? true : value
    })
  }, [id, setCustom, isFromShared])

  const handleCreate = useCallback(name => {
    setPattern(name)
    create(name)
  }, [create, setPattern])

  const handleChange = useCallback(option => {
    setPattern(option?.value || null)
  }, [setPattern])

  useEffect(() => {
    if (isFromShared) {
      setCustom(id, custom => {
        custom.stylePatternAutoUpdate = true
      })
    }
  }, [isFromShared, id, setCustom])

  useEffect(() => {
    if (name && !pattern) {
      create(name)
    }
  }, [create, name, pattern])

  return <InputGroup className="relative w-full flex gap-2 justify-between items-center">
    <Select
      variant="accent"
      creatable
      clearable
      value={pattern}
      options={options}
      onChange={handleChange}
      onCreateOption={handleCreate}
      isValidNewOption={value => !!value && isNaN(value)}
      promptTextCreator={value => `Create a new style pattern "${value}"`}
      disabled={isFromShared}
    />
    <ButtonGroup>
      <DefaultTooltip content={isFromShared ? "Auto Update is enabled for shared style patterns by default" : enabledUpdate ? "Disable Auto Update" : "Enable Auto Update"}>
        <Button variant="light" onClick={() => setEnabledUpdate(!enabledUpdate)} disabled={isFromShared}>
          {enabledUpdate ? <PencilOff size="14" /> : <Pencil size="14" />}
        </Button>
      </DefaultTooltip>
      <DefaultTooltip content="Update Style Pattern">
        <Button variant="light" onClick={() => update()}>
          <PaintbrushVertical size="14" />
        </Button>
      </DefaultTooltip>
    </ButtonGroup>
  </InputGroup>
}
